import { useState } from 'react'
import { useAuthStore } from '@/store/authStore'
import { Lock, Mail, User, Eye, EyeOff, ArrowRight, AlertCircle, Building2, KeyRound } from 'lucide-react'
import { Button } from '@/components/common/Button'
import { isSelfSignupAllowed, isPasswordLoginAllowed, isSsoLoginEnabled } from '@/config/authConfig'
import { isEntraAuthEnabled } from '@/lib/entraAuth'
import { hasPendingShareInvite } from '@/services/projectShareService'

/**
 * The email/password sign-in + sign-up form, factored out of LoginPage so it can
 * be reused in two places: the full-screen LoginPage (the real-auth wall) and the
 * homepage account menu's sign-in modal (the top-right entry point, #115/login).
 *
 * `onSuccess` fires after a sign-in that leaves no error (the modal closes on it).
 *
 * There is deliberately NO anonymous "Try Demo" shortcut: the app is gated, so
 * every visitor authenticates first, and demo mode is reachable only after an
 * approved login. Sign-up is a request — new accounts land in a pending state
 * until an administrator approves them (see AccessGate).
 */
export function LoginForm({
  onSuccess,
}: {
  onSuccess?: () => void
}) {
  const { signIn, signUp, signInWithSso, signInWithEntra, error, loading, clearError } = useAuthStore()
  const entraEnabled = isEntraAuthEnabled()
  const ssoEnabled = isSsoLoginEnabled() && !entraEnabled
  // Entra builds are Microsoft-sign-in only: no password form, no self-signup.
  const passwordEnabled = isPasswordLoginAllowed() && !entraEnabled
  // Arrived through a project share link: most invitees are new, so open on sign-up.
  const [invited] = useState(hasPendingShareInvite)
  const [mode, setMode] = useState<'signin' | 'signup'>(invited ? 'signup' : 'signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [organizationName, setOrganizationName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [signUpSuccess, setSignUpSuccess] = useState(false)

  function switchMode() {
    setMode(mode === 'signin' ? 'signup' : 'signin')
    clearError()
    setSignUpSuccess(false)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (mode === 'signup') {
      await signUp(email, password, fullName, organizationName || undefined)
      if (!useAuthStore.getState().error) {
        setSignUpSuccess(true)
      }
    } else {
      await signIn(email, password)
      if (!useAuthStore.getState().error) {
        onSuccess?.()
      }
    }
  }

  return (
    <>
      {/* Error alert */}
      {error && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-body-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* A project share link is waiting: it is redeemed after sign-in. */}
      {invited && !signUpSuccess && (
        <div className="mb-4 p-3 rounded-lg bg-brand-50 border border-brand-200 text-body-sm text-brand-700">
          You have been invited to a project in Tesseract XPM. Create an account, or sign in if you have one, and you will go
          straight to that project.
        </div>
      )}

      {/* Invited sign-up: no approval wait, the link approves them. */}
      {signUpSuccess && invited && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-body-sm text-green-700">
          Account created. If we sent you a confirmation email, confirm it in this browser, then sign in to open the project.
        </div>
      )}

      {/* Sign-up success — this is an access REQUEST, not instant entry. */}
      {signUpSuccess && !invited && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-body-sm text-green-700">
          Request submitted. Your account is awaiting administrator approval — you
          will be able to sign in once it is approved. If email confirmation is
          required, check your inbox first.
        </div>
      )}

      {/* Entra ID sign-in — the Azure enterprise build (VITE_AUTH_PROVIDER=entra).
          Inert on the demo/internal cockpit, same seam pattern as SSO below. */}
      {entraEnabled && (
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          disabled={loading}
          onClick={() => {
            void signInWithEntra().then(() => {
              if (!useAuthStore.getState().error) onSuccess?.()
            })
          }}
        >
          <span className="inline-flex items-center gap-2">
            <KeyRound size={16} /> Sign in with Microsoft
          </span>
        </Button>
      )}

      {/* SSO sign-in — product/fork builds with a configured IdP (VITE_SSO_DOMAIN).
          Inert on the demo/internal cockpit (isSsoLoginEnabled is product-only). */}
      {ssoEnabled && (
        <div className="space-y-4">
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            disabled={loading}
            onClick={() => signInWithSso()}
          >
            <span className="inline-flex items-center gap-2">
              <KeyRound size={16} /> Sign in with SSO
            </span>
          </Button>
          {passwordEnabled && (
            <div className="flex items-center gap-3 text-body-sm text-text-tertiary">
              <span className="h-px flex-1 bg-border" />
              or
              <span className="h-px flex-1 bg-border" />
            </div>
          )}
        </div>
      )}

      {passwordEnabled && (
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Full name (sign-up only) */}
        {mode === 'signup' && (
          <div>
            <label htmlFor="fullName" className="block text-body-sm font-medium text-text-primary mb-1.5">
              Full name
            </label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                id="fullName"
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="John Smith"
                className="w-full h-10 pl-10 pr-3 rounded-lg border border-border bg-surface-input text-body-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all"
              />
            </div>
          </div>
        )}

        {/* Organization (sign-up only) */}
        {mode === 'signup' && (
          <div>
            <label htmlFor="organizationName" className="block text-body-sm font-medium text-text-primary mb-1.5">
              Organization name
            </label>
            <div className="relative">
              <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                id="organizationName"
                type="text"
                required
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Acme Corp"
                className="w-full h-10 pl-10 pr-3 rounded-lg border border-border bg-surface-input text-body-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all"
              />
            </div>
          </div>
        )}

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-body-sm font-medium text-text-primary mb-1.5">
            Email address
          </label>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full h-10 pl-10 pr-3 rounded-lg border border-border bg-surface-input text-body-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all"
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-body-sm font-medium text-text-primary mb-1.5">
            Password
          </label>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full h-10 pl-10 pr-10 rounded-lg border border-border bg-surface-input text-body-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={loading}
          fullWidth
          trailingIcon={loading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <ArrowRight size={16} />}
        >
          {loading ? '' : (mode === 'signin' ? 'Sign in' : 'Create account')}
        </Button>
      </form>
      )}

      {/* Toggle mode — hidden when self-signup is disabled (product/fork builds):
          the wall is sign-in only; accounts come via SSO/admin provisioning. */}
      {isSelfSignupAllowed() && (
        <div className="mt-6 text-center text-body-sm text-text-secondary">
          {mode === 'signin' ? (
            <>
              Don't have an account?{' '}
              <button onClick={switchMode} className="text-brand-500 font-medium hover:text-brand-600">
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button onClick={switchMode} className="text-brand-500 font-medium hover:text-brand-600">
                Sign in
              </button>
            </>
          )}
        </div>
      )}
    </>
  )
}
